import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { useAppStore } from "@/lib/store";
import { Header } from "@/components/Header";
import { BottomNav } from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Wallet,
  ArrowUpFromLine,
  TrendingUp,
  CheckCircle2,
  X,
  ShieldCheck,
  ArrowDownToLine,
} from "lucide-react";
import { toast } from "sonner";
import { authGuard } from "@/lib/authGuard";
import { isValidEmail } from "@/lib/auth-utils";
import { WithdrawalLog } from "@/components/WithdrawalLog";

type WithdrawalMethod = "mobile_money" | "bank" | "paypal";

export const Route = createFileRoute("/wallet")({
  validateSearch: (search: Record<string, unknown>) => ({
    showFund: typeof search.showFund === "boolean" ? search.showFund : false,
  }),
  head: () => ({
    meta: [
      { title: "Wallet — PataTasks" },
      {
        name: "description",
        content: "Withdraw your testing rewards. Minimum withdrawal: $10.00 USD.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async () => {
    await authGuard();
  },
  component: WalletPage,
});

function WalletPage() {
  const { account, user, transactions, refreshAccount, withdrawFromWallet } = useAppStore();
  const search = useSearch({ from: "/wallet" });

  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawalMethod, setWithdrawalMethod] = useState<WithdrawalMethod>("mobile_money");
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState("");
  const [fullName, setFullName] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [paypalEmail, setPaypalEmail] = useState("");

  const rawBalance = account?.balance ?? 0;
  const balanceUsd = rawBalance / 100;
  const minWithdrawalUsd = 10;
  const progress = Math.min((balanceUsd / minWithdrawalUsd) * 100, 100);

  const stats = useMemo(() => {
    const tx = transactions || [];
    const totalWithdrawn = tx
      .filter((t) => t.type === "withdrawal")
      .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
    const completedTests = tx.filter((t) => t.type === "app_test" && t.status === "success").length;
    const withdrawalCount = tx.filter((t) => t.type === "withdrawal").length;
    const totalEarnings = rawBalance + totalWithdrawn;
    return {
      totalEarnings,
      completedTests,
      totalWithdrawn,
      withdrawalCount,
    };
  }, [transactions, rawBalance]);

  const resetWithdrawalForm = () => {
    setShowWithdraw(false);
    setWithdrawalMethod("mobile_money");
    setAmount("");
    setPhone("");
    setFullName("");
    setBankName("");
    setAccountNumber("");
    setPaypalEmail("");
  };

  useEffect(() => {
    refreshAccount();
  }, [refreshAccount]);

  const handleWithdraw = async () => {
    const num = Number(amount);
    if (num <= 0 || num > balanceUsd) {
      toast.error("Invalid amount");
      return;
    }
    if (num < minWithdrawalUsd) {
      toast.error(`Minimum withdrawal is $${minWithdrawalUsd}`);
      return;
    }
    if (withdrawalMethod === "mobile_money") {
      if (!phone || !isValidKenyanPhone(phone)) {
        toast.error("Enter a valid phone number");
        return;
      }
    } else if (withdrawalMethod === "bank") {
      if (
        !fullName.trim().split(/\s+/).filter(Boolean).length ||
        !bankName.trim() ||
        !/^[A-Za-z0-9-]{6,}$/.test(accountNumber.trim())
      ) {
        toast.error("Enter valid bank details");
        return;
      }
    } else if (!isValidEmail(paypalEmail)) {
      toast.error("Enter a valid PayPal email");
      return;
    }
    const baseAmount = Math.round(num * 100);
    const ok = await withdrawFromWallet(baseAmount, {
      method: withdrawalMethod,
      phone: withdrawalMethod === "mobile_money" ? phone : undefined,
      fullName: withdrawalMethod === "bank" ? fullName.trim() : undefined,
      bankName: withdrawalMethod === "bank" ? bankName.trim() : undefined,
      accountNumber: withdrawalMethod === "bank" ? accountNumber.trim() : undefined,
      paypalEmail: withdrawalMethod === "paypal" ? paypalEmail.trim() : undefined,
    });
    if (ok) {
      toast.success(
        user?.isAdmin === 1
          ? "Withdrawal completed successfully!"
          : "Withdrawal request received. Your balance will reflect within 2-3 hours.",
      );
      resetWithdrawalForm();
    } else {
      toast.error(useAppStore.getState().error ?? "Withdrawal failed");
    }
  };

  return (
    <div className="bg-linear-to-br from-slate-50 via-blue-50/20 to-background pb-20 md:pb-0">
      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="mb-6 rounded-3xl bg-linear-to-br from-blue-700 to-indigo-700 p-6 text-white shadow-xl">
          <h1 className="text-2xl font-bold">Wallet</h1>
          <p className="text-blue-100 text-sm mt-1">
            Withdraw your testing rewards. Minimum withdrawal: $10.00 USD.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
          <div className="relative overflow-hidden rounded-3xl bg-linear-to-br from-blue-700 to-indigo-700 p-6 text-white shadow-lg">
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="h-5 w-5 opacity-90" />
                <span className="text-sm font-medium opacity-90">Available Balance</span>
              </div>
              <p className="text-3xl font-bold tabular-nums tracking-tight">
                ${balanceUsd.toFixed(2)}
              </p>
              {user?.isAdmin !== 1 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-blue-100 mb-1">
                    <span>Withdrawal progress</span>
                    <span>${minWithdrawalUsd.toFixed(2)} goal</span>
                  </div>
                  <div className="h-2 rounded-full bg-blue-900/40">
                    <div
                      className="h-2 rounded-full bg-emerald-400 transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  {balanceUsd < minWithdrawalUsd && (
                    <p className="text-xs text-blue-100 mt-1">
                      ${(minWithdrawalUsd - balanceUsd).toFixed(2)} away from withdrawal
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-3xl border border-blue-100 bg-linear-to-br from-blue-50 to-blue-25 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              <p className="text-xs text-blue-600 uppercase font-semibold tracking-wide">
                Total Earnings
              </p>
            </div>
            <p className="text-2xl font-bold tabular-nums text-foreground">
              ${(stats.totalEarnings / 100).toFixed(2)}
            </p>
          </div>

          <div className="rounded-3xl border border-emerald-100 bg-linear-to-br from-emerald-50 to-emerald-25 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <p className="text-xs text-emerald-600 uppercase font-semibold tracking-wide">
                Tests Completed
              </p>
            </div>
            <p className="text-2xl font-bold tabular-nums text-foreground">
              {stats.completedTests}
            </p>
          </div>
          <div className="rounded-3xl border border-orange-100 bg-linear-to-br from-orange-50 to-orange-25 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <ArrowDownToLine className="h-4 w-4 text-orange-600" />
              <p className="text-xs text-orange-600 uppercase font-semibold tracking-wide">
                Withdrawals
              </p>
            </div>
            <p className="text-2xl font-bold tabular-nums text-foreground">
              {stats.withdrawalCount}
            </p>
          </div>
        </div>

        <div className="mb-6">
          <Button
            onClick={() => setShowWithdraw(true)}
            className="w-full rounded-xl bg-blue-600 hover:bg-green-600 text-white"
            size="lg"
            disabled={balanceUsd < minWithdrawalUsd}
          >
            <ArrowUpFromLine className="h-4 w-4" /> Withdraw Funds
          </Button>
        </div>

        <div className="mb-6 rounded-2xl bg-linear-to-br from-slate-50 to-slate-100 p-5 border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-700">Withdrawal Methods</p>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700">
              Secure
            </span>
          </div>
          <p className="text-xs text-slate-600">
            Withdraw your earnings to M-Pesa, bank account, or PayPal. All withdrawals are processed
            securely via IntaSend.
          </p>
        </div>

        <WithdrawalLog
          withdrawals={transactions.filter((t) => t.type === "withdrawal")}
          title="Withdrawal Log History"
          emptyMessage="No withdrawals yet. Start earning and withdraw your rewards!"
        />
      </div>
      <BottomNav />

      {showWithdraw && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-foreground/40 p-4">
          <div className="w-full max-w-sm rounded-2xl border border-border/50 bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground">Withdraw Funds</h2>
              <button
                onClick={() => setShowWithdraw(false)}
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-4">
              <Label className="mb-1.5 block text-sm font-medium text-slate-700">
                Amount (USD)
              </Label>
              <Input
                type="number"
                placeholder="Min $10.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-12"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Available: ${balanceUsd.toFixed(2)} • Minimum: $10.00
              </p>
            </div>

            <div className="mb-4">
              <Label className="mb-1.5 block text-sm font-medium text-slate-700">Method</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: "mobile_money", label: "M-Pesa" },
                  { value: "bank", label: "Bank" },
                  { value: "paypal", label: "PayPal" },
                ].map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setWithdrawalMethod(m.value as WithdrawalMethod)}
                    className={`rounded-xl border-2 px-3 py-2 text-xs font-semibold transition-all ${
                      withdrawalMethod === m.value
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {withdrawalMethod === "mobile_money" && (
              <div className="mb-4">
                <Label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Phone Number
                </Label>
                <Input
                  type="tel"
                  placeholder="0712345678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-12"
                />
              </div>
            )}

            {withdrawalMethod === "bank" && (
              <>
                <div className="mb-3">
                  <Label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Full Name
                  </Label>
                  <Input
                    placeholder="John Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="h-12"
                  />
                </div>
                <div className="mb-3">
                  <Label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Bank Name
                  </Label>
                  <Input
                    placeholder="Equity Bank"
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                    className="h-12"
                  />
                </div>
                <div className="mb-4">
                  <Label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Account Number
                  </Label>
                  <Input
                    placeholder="123456789"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    className="h-12"
                  />
                </div>
              </>
            )}

            {withdrawalMethod === "paypal" && (
              <div className="mb-4">
                <Label className="mb-1.5 block text-sm font-medium text-slate-700">
                  PayPal Email
                </Label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={paypalEmail}
                  onChange={(e) => setPaypalEmail(e.target.value)}
                  className="h-12"
                />
              </div>
            )}

            <Button
              onClick={handleWithdraw}
              disabled={!amount || Number(amount) < minWithdrawalUsd}
              className="w-full h-12 bg-blue-600 hover:bg-green-600 text-white"
            >
              Withdraw ${amount || "0.00"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function isValidKenyanPhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, "");
  return digits.length === 10 && digits.startsWith("0");
}
